import { useEffect, useState } from "react";
import DeviceInfo from "react-native-device-info";
import { useNetInfo } from "@react-native-community/netinfo";
import Constants from "@lib/utilities/Constants";
import Storage from "@lib/utilities/storage/StorageFunctions";
import { appUpdateRequired } from "@lib/utilities/HelperFunctions";
import { getAppUpdateInfo } from "@lib";
import { messages } from "../../../src/resources/strings";
import { Platform } from "react-native";

export const useAppUpdate = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [isUpdateVisible, setIsUpdateVisible] = useState(false);
  const [appUpdateApiData, setAppUpdateApiData] = useState<any>({});
  const [updateError, setUpdateError] = useState<string | null>(
    messages.error.noError,
  );

  const { isConnected } = useNetInfo();
  const appCurrentVersion = DeviceInfo.getVersion();

  const checkIfUpdateSkipped24Hr = async (): Promise<boolean> => {
    try {
      const skipTime = await Storage.getData(
        Constants.LocalStoreConstant.APP_UPDATE_SKIPPED,
      );
      if (!skipTime) return true;

      const skippedDate = new Date(skipTime);
      if (isNaN(skippedDate.getTime())) return true;

      const timeDifference = Date.now() - skippedDate.getTime();
      const twentyFourHoursMs = 24 * 60 * 60 * 1000;

      return timeDifference > twentyFourHoursMs;
    } catch (error) {
      return true;
    }
  };

  const handleSkipUpdate = async () => {
    setIsUpdateVisible(false);
    try {
      await Storage.storeData(
        Constants.LocalStoreConstant.APP_UPDATE_SKIPPED,
        new Date().toISOString(),
      );
    } catch (error) {
      console.error("Failed to save skip time:", error);
    }
  };

  useEffect(() => {
    const checkUpdateFlow = async () => {
      if (isConnected === false) {
        setUpdateError(messages.error.youAreOffline);
        setIsLoading(false);
        return;
      }

      if (isConnected === null) return;
      setIsLoading(true);
      try {
        const res = await getAppUpdateInfo();

        if (!res?.enabledFeature) {
          setIsUpdateVisible(false);
          return;
        }

        setAppUpdateApiData(res);

        const isRequired = appUpdateRequired(
          appCurrentVersion,
          res?.latest_version,
        );
        const shouldShowBasedOnTime = await checkIfUpdateSkipped24Hr();
        const isForced = !!res?.force_update;

        // 2. Second Check: Show if required AND (is forced OR hasn't been skipped recently)
        if (isRequired && (isForced || shouldShowBasedOnTime)) {
          setIsUpdateVisible(true);
        } else {
          setIsUpdateVisible(false);
        }
      } catch (error) {
        console.error("Error fetching app update info:", error);
      } finally {
        setIsLoading(false);
      }
    };

    if (Platform.OS !== "web") {
      checkUpdateFlow();
    }
  }, [isConnected]);

  return {
    updateAvailable: isUpdateVisible,
    updateTitle: appUpdateApiData?.title || "Update Available",
    updateDescription:
      appUpdateApiData?.description || "A new version of the app is available.",
    updateVersion: appUpdateApiData?.latest_version,
    updateReleaseNotes: appUpdateApiData?.release_notes,
    forced: !!appUpdateApiData?.force_update,
    handleSkipUpdate,
    isLoading,
    updateError,
    setUpdateError,
  };
};
